import { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useEffect } from 'react';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact Exalt Ventures Ltd.",
      "description": "Get in touch with Exalt Ventures Ltd. for inquiries about our brands and retail operations.",
      "url": `${import.meta.env.VITE_SITE_URL || "https://exaltventures.com"}/contact`
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('company', formData.company);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4ndrlijh8g73q739s80', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', company: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl font-serif text-[#003264] mb-6 leading-tight">Contact Us</h1>
            <p className="text-base text-[#003264] leading-relaxed">
              We welcome inquiries from brand partners, franchise opportunities, and customer service requests. Reach out to our team today.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 bg-[#FAFAFA]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white p-12">
              <h2 className="text-2xl font-serif text-[#003264] mb-6 text-center">Get in Touch</h2>
              <p className="text-sm text-[#003264] text-center mb-10 leading-relaxed">
                Please complete the form below and we will respond to your inquiry promptly.
              </p>
              
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-xs font-medium text-[#003264] mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-[#003264]/20 text-sm text-[#003264] focus:outline-none focus:border-[#64C8FA] transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-xs font-medium text-[#003264] mb-2">
                    Company *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-[#003264]/20 text-sm text-[#003264] focus:outline-none focus:border-[#64C8FA] transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-[#003264] mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-[#003264]/20 text-sm text-[#003264] focus:outline-none focus:border-[#64C8FA] transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-medium text-[#003264] mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 border border-[#003264]/20 text-sm text-[#003264] focus:outline-none focus:border-[#64C8FA] transition-colors resize-none"
                  />
                  <p className="text-xs text-[#003264]/60 mt-1">Maximum 500 characters</p>
                </div>

                {submitStatus === 'success' && (
                  <div className="p-3 bg-[#64C8FA]/10 border border-[#64C8FA]">
                    <p className="text-sm text-[#003264] text-center">Thank you for your message. We will be in touch shortly.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-3 bg-red-50 border border-red-200">
                    <p className="text-sm text-red-800 text-center">There was an error submitting your message. Please try again.</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-3 bg-[#003264] text-white text-xs font-medium hover:bg-[#003264]/90 transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-serif text-[#003264] text-center mb-12">Our Locations</h2>
            <div className="grid md:grid-cols-3 gap-10">
              <div className="text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-5">
                  <i className="ri-map-pin-line text-3xl text-[#64C8FA]"></i>
                </div>
                <h3 className="text-xl font-serif text-[#003264] mb-3">Libya</h3>
                <p className="text-sm text-[#003264] leading-relaxed">
                  Multiple retail locations across Libya serving customers since 2008.
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-5">
                  <i className="ri-map-pin-line text-3xl text-[#64C8FA]"></i>
                </div>
                <h3 className="text-xl font-serif text-[#003264] mb-3">Algeria</h3>
                <p className="text-sm text-[#003264] leading-relaxed">
                  Strategic store placements in premium Algerian shopping destinations.
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-5">
                  <i className="ri-map-pin-line text-3xl text-[#64C8FA]"></i>
                </div>
                <h3 className="text-xl font-serif text-[#003264] mb-3">Malta</h3>
                <p className="text-sm text-[#003264] leading-relaxed">
                  Four flagship Dune London stores across Malta's premier retail areas.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
